import AndroidApp from "./pages/AndroidApp";
import { lazy, Suspense, useEffect, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { captureMessage } from "./lib/sentry";
// i18n 2026-08-27 — path-prefixed locales. Importing "./i18n" also
// initialises i18next (inline resources, synchronous) before render.
import { LOCALES, isSupportedLocale } from "./i18n";
import { LocaleScope } from "./i18n/LocaleScope";

// Lazy-load EVERY route — Vite splits each into its own chunk so the
// initial JS payload stays small. Previously the main bundle included
// Landing (903 LOC) + Reading (850 LOC) + their chart-component imports
// even for users who only ever see the homepage, contributing ~1.1 MB
// to the entry chunk. The two visitors-hit-first routes are no longer
// special-cased: they still fetch immediately after the entry chunk
// parses, but no parse work is wasted on them for users who navigate
// directly to a deep route.
// Web redesign 2026-07-09: `/` is living Today (5-tab shell). Old marketing
// Landing preserved at `/landing` for SEO/FAQ deep links.
const Landing = lazy(() => import("./pages/Landing"));
const Reading = lazy(() => import("./pages/Reading"));
const Cast            = lazy(() => import("./pages/Cast"));
const Council         = lazy(() => import("./pages/Council"));
const ThreeTraditionCouncil = lazy(() => import("./pages/ThreeTraditionCouncil"));
const PlanetaryStatus = lazy(() => import("./pages/PlanetaryStatus"));
const DashaAnalogs    = lazy(() => import("./pages/DashaAnalogs"));
const RulesLibrary    = lazy(() => import("./pages/RulesLibrary"));
const SavedCharts     = lazy(() => import("./pages/SavedCharts"));
const LifePredictions = lazy(() => import("./pages/LifePredictions"));
const Journey            = lazy(() => import("./pages/Journey"));
const Today            = lazy(() => import("./pages/Today"));
const Ask              = lazy(() => import("./pages/Ask"));
const Listen           = lazy(() => import("./pages/Listen"));
const Discover         = lazy(() => import("./pages/Discover"));
const Privacy         = lazy(() => import("./pages/Privacy"));
const Terms           = lazy(() => import("./pages/Terms"));
const Benchmark       = lazy(() => import("./pages/Benchmark"));
const Radio           = lazy(() => import("./pages/Radio"));
const Subscription    = lazy(() => import("./pages/Subscription"));
const Alerts          = lazy(() => import("./pages/Alerts"));
const LifeMusic       = lazy(() => import("./pages/LifeMusic"));
const ModulePage      = lazy(() => import("./pages/ModulePage"));
const AccountSettings = lazy(() => import("./pages/AccountSettings"));
const DashaSystems    = lazy(() => import("./pages/DashaSystems"));
const ComputePage     = lazy(() => import("./pages/ComputePage"));
const CaseLibrary     = lazy(() => import("./pages/CaseLibrary"));
const ProfessionCaseStudy = lazy(() => import("./pages/ProfessionCaseStudy"));
const Library         = lazy(() => import("./pages/Library"));
const LibraryBook     = lazy(() => import("./pages/LibraryBook"));
const LibraryChapter  = lazy(() => import("./pages/LibraryChapter"));
const DiscoveryArticle = lazy(() => import("./pages/DiscoveryArticle"));
const DiscoveryIndex = lazy(() => import("./pages/DiscoveryIndex"));
const Movement        = lazy(() => import("./pages/Movement"));
const WatchOverview   = lazy(() => import("./pages/WatchOverview"));
const Archive         = lazy(() => import("./pages/Archive"));
const CelebrityDasha  = lazy(() => import("./pages/CelebrityDasha"));
const CelebrityAssessment = lazy(() => import("./pages/CelebrityAssessment"));
const CelebrityCompare = lazy(() => import("./pages/CelebrityCompare"));
const SpaceLab = lazy(() => import("./pages/SpaceLab"));
const Sky = lazy(() => import("./pages/Sky"));
const RagaMethodology = lazy(() => import("./pages/RagaMethodology"));
const Station          = lazy(() => import("./pages/Station"));
const Planner = lazy(() => import("./pages/Planner"));
const CalendarPage = lazy(() => import("./pages/CalendarPage"));
// Mundane lives under Discover tab (left the Watch bezel in redesign).
const Mundane = lazy(() => import("./pages/Mundane"));
const MundaneStudio = lazy(() => import("./pages/MundaneStudio"));
// SEO content pages (audit 2026-07): explanatory front-door for visitors
// who want to understand the engine before entering birth data.
const HowItWorks    = lazy(() => import("./pages/HowItWorks"));
const SampleReading = lazy(() => import("./pages/SampleReading"));
const Faq           = lazy(() => import("./pages/Faq"));
const Houses           = lazy(() => import("./pages/Houses"));
const EphemerisPage    = lazy(() => import("./pages/EphemerisPage"));
const PanchangPage     = lazy(() => import("./pages/PanchangPage"));
const PredictionPage   = lazy(() => import("./pages/PredictionPage"));
const ExportPage       = lazy(() => import("./pages/ExportPage"));
const ImportPage       = lazy(() => import("./pages/ImportPage"));
const SearchPage       = lazy(() => import("./pages/SearchPage"));
const ContactPage      = lazy(() => import("./pages/ContactPage"));
const AdvancedToolsPage = lazy(() => import("./pages/AdvancedToolsPage"));
const Rectify          = lazy(() => import("./pages/Rectify"));
const NotFound         = lazy(() => import("./pages/NotFound"));

// Minimal in-shell fallback during chunk fetch. Skeleton block instead of
// "loading…" text so the page layout is reserved and the user sees the
// brand aesthetic (brass-to-walnut gradient sweep) instead of a spinner.
import ErrorToast from "./components/ErrorToast";
import { getActiveChartId } from "./lib/chartSession";

function LegacyWatchRedirect() {
  const chartId = getActiveChartId();
  return <Navigate to={chartId ? `/reading/${chartId}` : "/cast"} replace />;
}

function LegacyCelebrityRedirect() {
  const { id = "vesper" } = useParams<{ id: string }>();
  const normalized = id
    .replace(/_/g, "-")
    .replace(/-\d+$/, "")
    .replace(/^maradona$/, "diego-maradona");
  return <Navigate to={`/celebrity/${normalized}`} replace />;
}

/**
 * RouteBreadcrumb — fires a Sentry `captureMessage("nav.<chapter>", "info")`
 * on every client-side route change. Sits between <Suspense> and <Routes>
 * so it sees every navigation without each page having to wire it.
 *
 * Why breadcrumb + not captureException: we want a trail on the event
 * timeline ("user visited /cast → /reading/abc → /movement") so a later
 * exception has context. Breadcrumbs don't generate events on their own;
 * they're attached to whatever exception follows.
 *
 * Why pathname only: no chart_ids, no celebrity ids, no query strings
 * (those can contain user-typed free text). Just the chapter-shaped path.
 * Pathnames like `/reading/abc-123-xyz` are coarse enough that they don't
 * single out an individual user — the chart id is meaningless without
 * the corresponding chart data.
 */
function RouteBreadcrumb({ children }: { children: ReactNode }) {
  const location = useLocation();
  const lastPath = useRef<string>("");
  useEffect(() => {
    // Skip the first render (no prior path). Only fire on actual change.
    if (lastPath.current === location.pathname) return;
    lastPath.current = location.pathname;
    // Trim to the first two path segments: "/library/discoveries" → "library",
    // "/reading/abc" → "reading". Coarse enough to be a stable chapter tag.
    // i18n: a leading locale prefix (/hi/movement) is routing context, not
    // a chapter — strip it so breadcrumbs stay chapter-shaped.
    const segments = location.pathname.split("/").filter(Boolean);
    const chapterSegments = isSupportedLocale(segments[0]) ? segments.slice(1) : segments;
    const chapter = chapterSegments[0] || "root";
    captureMessage(`nav.${chapter}`, "info");
  }, [location.pathname]);
  return <>{children}</>;
}

function RouteFallback() {
  const { t } = useTranslation();
  return (
    <main id="main" className="page-shell">
      <div className="min-h-screen flex flex-col items-center justify-center gap-5 px-6">
        <p className="t-eyebrow opacity-70">{t("ui.web.app.opening_page")}</p>
        <div className="w-64 max-w-full space-y-3">
          <div className="skeleton h-2 w-11/12 mx-auto" />
          <div className="skeleton h-2 w-8/12 mx-auto" />
          <div className="skeleton h-2 w-10/12 mx-auto" />
        </div>
      </div>
    </main>
  );
}

/**
 * AppRoutes — the single route table for the whole app, mounted as a
 * DESCENDANT <Routes> under every locale branch (see App below). react-
 * router 6 resolves a descendant <Routes> against the URL remainder
 * after the parent match, so "/hi/movement" and "/movement" both land
 * on path="movement" here without duplicating the table.
 */
function AppRoutes() {
  return (
    <Routes>
        {/* 5-tab shell (web redesign 2026-07-09) */}
        <Route path="/" element={<Today />} />
        <Route path="/landing" element={<Landing />} />
        <Route path="/landing/legacy" element={<Landing />} />
        <Route path="/clock" element={<Landing />} />
        <Route path="/ask" element={<Ask />} />
        <Route path="/listen" element={<Listen />} />
        <Route path="/discover" element={<Discover />} />
        <Route path="/cast" element={<Cast />} />
        <Route path="/watch" element={<LegacyWatchRedirect />} />
        {/* Watch overview landing — belongs to NO chapter (6 cards + Start with Face). */}
        <Route path="/watch/:id" element={<WatchOverview />} />
        <Route path="/reading/:id" element={<Reading />} />
        <Route path="/today" element={<Today />} />
        <Route path="/today/:id" element={<Today />} />
        <Route path="/predictions" element={<LifePredictions />} />
        <Route path="/predictions/:id" element={<LifePredictions />} />
        <Route path="/journey" element={<Journey />} />
        <Route path="/journey/:id" element={<Journey />} />
        <Route path="/movement" element={<Movement />} />
        <Route path="/movement/:id" element={<Movement />} />
        <Route path="/planner" element={<Planner />} />
        <Route path="/planner/:id" element={<Planner />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/calendar/:id" element={<CalendarPage />} />
        <Route path="/archive" element={<Archive />} />
        <Route path="/archive/:id" element={<Archive />} />
        {/* Synastry still cut (one-way model). Horary remounted as /ask. */}
        <Route path="/synastry" element={<NotFound />} />
        <Route path="/synastry/:id" element={<NotFound />} />
        <Route path="/horary" element={<Navigate to="/ask" replace />} />
        <Route path="/council" element={<Council />} />
        <Route path="/council/:id" element={<Council />} />
        <Route path="/three-council" element={<ThreeTraditionCouncil />} />
        <Route path="/three-council/:id" element={<ThreeTraditionCouncil />} />
        <Route path="/planetary-status" element={<PlanetaryStatus />} />
        <Route path="/planetary-status/:id" element={<PlanetaryStatus />} />
        <Route path="/dasha-analogs" element={<DashaAnalogs />} />
        <Route path="/dasha-analogs/:id" element={<DashaAnalogs />} />
        <Route path="/best-dates" element={<NotFound />} />
        <Route path="/rules" element={<RulesLibrary />} />
        <Route path="/saved" element={<SavedCharts />} />
        <Route path="/rectify" element={<Rectify />} />
        <Route path="/android" element={<AndroidApp />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        {/* SEO content pages (audit 2026-07) */}
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/sample-reading" element={<SampleReading />} />
        <Route path="/faq" element={<Faq />} />
        <Route path="/space-lab" element={<SpaceLab />} />
        <Route path="/benchmark" element={<Benchmark />} />
        <Route path="/radio/:id" element={<Radio />} />
        <Route path="/radio" element={<Radio />} />
        {/*
          THE BROADCAST CHANNEL, not the product surface.
          ADR-017 section 4: the server-rendered path "remains valid for the
          public channel -- render once, serve to many" and is ruled out for
          "the per-listener product surface". These routes are kept for that
          reason and for links not yet moved. The product's own door --
          /listen -- resolves to /station, which synthesises on the device.
        */}
        <Route path="/transit-radio/:id" element={<Radio />} />
        <Route path="/transit-radio" element={<Radio />} />
        <Route path="/life-music/:id" element={<LifeMusic />} />
        <Route path="/life-music" element={<LifeMusic />} />
        <Route path="/sound/raga-methodology" element={<RagaMethodology />} />
        <Route path="/station" element={<Station />} />
        <Route path="/station/:chartId" element={<Station />} />
        <Route path="/plans" element={<Subscription />} />
        <Route path="/alerts" element={<Alerts />} />
        <Route path="/features/:moduleKey" element={<ModulePage />} />
        <Route path="/account" element={<AccountSettings />} />
        <Route path="/cases" element={<CaseLibrary />} />
        <Route path="/cases/:profession" element={<ProfessionCaseStudy />} />
        <Route path="/library" element={<Library />} />
        <Route path="/library/book/:slug" element={<LibraryBook />} />
        <Route path="/library/book/:slug/chapter/:chapterId" element={<LibraryChapter />} />
        <Route path="/library/discoveries" element={<DiscoveryIndex />} />
        <Route path="/library/celebrities/:id" element={<LegacyCelebrityRedirect />} />
        <Route path="/library/discoveries/:slug" element={<DiscoveryArticle />} />
        <Route path="/celebrity/:id/assessment" element={<CelebrityAssessment />} />
        <Route path="/celebrity/:id" element={<CelebrityAssessment />} />
        <Route path="/celebrities" element={<Navigate to="/cases" replace />} />
        <Route path="/celebrities/:id" element={<CelebrityDasha />} />
        <Route path="/celebrities/compare" element={<CelebrityCompare />} />
        <Route path="/dashas" element={<DashaSystems />} />
        <Route path="/dashas/:id" element={<DashaSystems />} />
        <Route path="/compute" element={<ComputePage />} />
        <Route path="/compute/:id" element={<ComputePage />} />
        <Route path="/sky" element={<Sky />} />
        <Route path="/sky/:id" element={<Sky />} />
        {/* Mundane chapter VII — session-2026-07-01. Chart-less, world
            astrology. The `:id` route exists so the bezel works the same
            way as every other chapter (links to /mundane/<chartId> when
            chart-bound, but /mundane alone is sufficient). */}
        <Route path="/mundane" element={<Mundane />} />
        <Route path="/mundane-studio" element={<MundaneStudio />} />
        <Route path="/mundane/countries" element={<Mundane />} />
        <Route path="/mundane/benchmark" element={<Mundane />} />
        <Route path="/mundane/:id" element={<Mundane />} />
        <Route path="/event-timing" element={<NotFound />} />
        <Route path="/event-timing/:id" element={<NotFound />} />
        <Route path="/houses" element={<Houses />} />
        <Route path="/houses/:id" element={<Houses />} />
        <Route path="/ephemeris" element={<EphemerisPage />} />
        <Route path="/ephemeris/:id" element={<EphemerisPage />} />
        <Route path="/panchang" element={<PanchangPage />} />
        <Route path="/panchang/:id" element={<PanchangPage />} />
        <Route path="/relationship" element={<NotFound />} />
        <Route path="/relationship/:id" element={<NotFound />} />
        <Route path="/prediction" element={<PredictionPage />} />
        <Route path="/prediction/:id" element={<PredictionPage />} />
        <Route path="/export" element={<ExportPage />} />
        <Route path="/export/:id" element={<ExportPage />} />
        <Route path="/import" element={<ImportPage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/tools" element={<AdvancedToolsPage />} />
        <Route path="/tools/:id" element={<AdvancedToolsPage />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
  );
}

/**
 * App — locale-aware shell.
 *
 * One route pattern per SUPPORTED locale prefix (`/:code/*`) plus the
 * legacy unprefixed catch-all. Both mount LocaleScope + the same
 * AppRoutes table (descendant <Routes> strips the prefix automatically).
 *
 * Deliberately NOT `/:locale?`: an optional first segment would swallow
 * single-segment routes ("/cast" → locale="cast"). Enumerating the
 * supported codes keeps every legacy URL byte-compatible and lets
 * unknown prefixes (e.g. "/xx/…") fall through to the legacy branch.
 *
 * No redirects: legacy unprefixed URLs render directly. In-app <Link>s
 * still emit unprefixed paths (pre-existing behaviour); the LocaleScope
 * detect chain keeps the chosen language sticky across them. Full
 * locale-aware link rewriting is a follow-up.
 */
export default function App() {
  const location = useLocation();

  return (
    <>
      <ErrorToast />
      <Suspense key={location.pathname} fallback={<RouteFallback />}>
      <RouteBreadcrumb>
      <Routes>
        {/* Locale-prefixed branches — generated from LOCALES metadata */}
        {LOCALES.map(({ code }) => (
          <Route
            key={code}
            path={`/${code}/*`}
            element={
              <LocaleScope pathLocale={code}>
                <AppRoutes />
              </LocaleScope>
            }
          />
        ))}
        {/* Legacy unprefixed URLs — render exactly as before i18n */}
        <Route
          path="*"
          element={
            <LocaleScope>
              <AppRoutes />
            </LocaleScope>
          }
        />
      </Routes>
      </RouteBreadcrumb>
    </Suspense>
    </>
  );
}
